import { OPACITY_AD_CONTROLLING } from "@theme";
import { LayoutChangeEvent } from "react-native";
import { GestureDetector, Gesture } from "react-native-gesture-handler";
import {
  useAnimatedStyle,
  useSharedValue,
  SharedValue,
} from "react-native-reanimated";
import { scheduleOnRN } from "react-native-worklets";
import { type PlaybackLimitConfig } from "@utils/player";
import LBox from "../LBox";

interface ProgressBarProps {
  seekable: boolean;
  duration: number;
  setIsSeeking: (value: boolean) => void;
  startSeek: () => void;
  endSeek: (value: number) => void;
  seek: (value: number) => void;
  playbackLimitConfig: PlaybackLimitConfig;
  setSeekingPosition: (value: number) => void;
  progressValue: SharedValue<number>;
}

export const ProgressBar = ({
  seekable,
  duration,
  setIsSeeking,
  startSeek,
  endSeek,
  seek,
  playbackLimitConfig,
  setSeekingPosition,
  progressValue,
}: ProgressBarProps) => {
  const barWidth = useSharedValue(0);
  const isDraggingUI = useSharedValue(false);

  const onLayout = (event: LayoutChangeEvent) => {
    barWidth.value = event.nativeEvent.layout.width;
  };

  const handleSeek = (x: number) => {
    "worklet";
    if (!seekable || duration <= 0 || barWidth.value === 0) return;

    let newPercent = (x / barWidth.value) * 100;
    newPercent = Math.max(0, Math.min(100, newPercent));

    const newPosition = (newPercent / 100) * duration;

    // Apply limit if enabled
    const finalPosition = playbackLimitConfig.enabled
      ? Math.min(newPosition, playbackLimitConfig.limitSeconds)
      : newPosition;

    progressValue.set(finalPosition);

    // Low-frequency JS thread updates for store/parent state
    scheduleOnRN(seek, finalPosition);
    scheduleOnRN(setSeekingPosition, finalPosition);
  };

  const gesture = Gesture.Pan()
    .enabled(seekable)
    .onStart((e) => {
      isDraggingUI.value = true;
      scheduleOnRN(setIsSeeking, true);
      scheduleOnRN(startSeek);
      handleSeek(e.x);
    })
    .onUpdate((e) => {
      handleSeek(e.x);
    })
    .onEnd(() => {
      const finalPosition = progressValue.value;
      scheduleOnRN(endSeek, finalPosition);
      scheduleOnRN(setIsSeeking, false);
      isDraggingUI.value = false;
    });

  const tapGesture = Gesture.Tap()
    .enabled(seekable)
    .onStart((e) => {
      isDraggingUI.value = true;
      scheduleOnRN(setIsSeeking, true);
      scheduleOnRN(startSeek);
      handleSeek(e.x);
      const finalPosition = progressValue.value;
      scheduleOnRN(endSeek, finalPosition);
      scheduleOnRN(setIsSeeking, false);
      isDraggingUI.value = false;
    });

  const composed = Gesture.Exclusive(gesture, tapGesture);

  const animatedStyle = useAnimatedStyle(() => {
    const percent = duration > 0 ? (progressValue.value / duration) * 100 : 0;
    return { width: `${percent}%` };
  }, [duration]);

  return (
    <GestureDetector gesture={composed}>
      <LBox
        width="100%"
        height={20}
        justifyContent="center"
        onLayout={onLayout}
        style={{ opacity: seekable ? 1 : OPACITY_AD_CONTROLLING }}
      >
        <LBox
          width="100%"
          height={4}
          borderRadius="m"
          backgroundColor="progressTrackBg"
          overflow="hidden"
        >
          {/* On web, animating a LinearGradient's width (esp. percentage strings)
              is unreliable. Instead, animate a plain view as a "mask" and keep
              the gradient at full width inside it. */}
          <LBox
            height="100%"
            overflow="hidden"
            borderRadius="m"
            style={animatedStyle}
          >
            <LBox
              width="100%"
              height="100%"
              borderRadius="m"
              backgroundColor={[
                "buttonBackgroundPrimaryLeft",
                "buttonBackgroundPrimaryRight",
              ]}
              // backgroundColor="debug"
              gradientDirection="horizontal"
            />
          </LBox>
        </LBox>
      </LBox>
    </GestureDetector>
  );
};
